import React, { useState, useEffect } from 'react';
import { X, User as UserIcon, Check, Plus, AlertCircle, Copy, CheckCheck, Loader2, Sparkles, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { Department } from '../../../shared/types.ts';
import { useEscapeKey } from '../../hooks/useEscapeKey.ts';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInviteSuccess?: () => void;
}

type InviteRoleType = 'member' | 'limited_member' | 'guest' | 'admin' | 'custom';

interface RoleOption {
  id: InviteRoleType;
  name: string;
  badge?: string;
  badgeColor?: string;
  description: string;
}

const ROLE_OPTIONS: RoleOption[] = [
  {
    id: 'member',
    name: 'Member',
    description: 'Can access all public items in your Workspace.',
  },
  {
    id: 'limited_member',
    name: 'Limited Member',
    badge: 'Chat Collaborator',
    badgeColor: 'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300',
    description: 'Can only access items shared with them.',
  },
  {
    id: 'guest',
    name: 'Guest',
    description: "Can't use all features or be added to Spaces. Can only access items shared with them.",
  },
  {
    id: 'admin',
    name: 'Admin',
    description: 'Can manage Spaces, People, Billing and other Workspace settings.',
  },
];

export const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose, onInviteSuccess }) => {
  const { token, user } = useAuth();
  const [emailInput, setEmailInput] = useState('');
  const [selectedRole, setSelectedRole] = useState<InviteRoleType>('member');
  const [customRoleName, setCustomRoleName] = useState('');
  const [isRoleMenuOpen, setIsRoleMenuOpen] = useState(false);
  const [isAddingCustomRole, setIsAddingCustomRole] = useState(false);
  const [selectedDeptId, setSelectedDeptId] = useState<string>('');
  const [departments, setDepartments] = useState<Department[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const [inviteResult, setInviteResult] = useState<{
    invited: Array<{
      email: string;
      fullName: string;
      role: string;
      generatedPassword: string;
      verificationCode: string;
      emailDelivered: boolean;
    }>;
    alreadyMembers: string[];
    totalInvited: number;
    message: string;
  } | null>(null);

  // Fetch departments when modal opens
  useEffect(() => {
    if (isOpen && token) {
      fetch('/api/team/departments', { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((d) => {
          const raw = d.departments || [];
          const dummyNames = [
            'insuretech engineering',
            'cloud infrastructure & cybersecurity',
            'insurance solutions & architecture',
            'regulatory compliance & qa',
          ];
          setDepartments(
            raw.filter((dept: { id: string; name: string }) => {
              const nameLower = (dept.name || '').toLowerCase().trim();
              return !dummyNames.includes(nameLower) && !dept.id.startsWith('dept-dummy-');
            })
          );
        })
        .catch(() => {});
    }
  }, [isOpen, token]);

  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  const currentRoleConfig = ROLE_OPTIONS.find((r) => r.id === selectedRole) || {
    id: 'custom' as InviteRoleType,
    name: customRoleName || 'Custom Role',
    description: 'Custom designated workspace permissions.',
  };

  // Parse entered emails into a clean list
  const parsedEmails = emailInput
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

  const handleCopy = (text: string, keyName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyName);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const handleSendInvite = async () => {
    if (parsedEmails.length === 0) {
      setError('Please enter at least one valid email address.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setInviteResult(null);

    try {
      const res = await fetch('/api/team/invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          emails: parsedEmails,
          role: selectedRole === 'custom' ? 'member' : selectedRole,
          customRoleName: selectedRole === 'custom' ? customRoleName.trim() : undefined,
          departmentId: selectedDeptId || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to send invitations.');
      }

      setInviteResult({
        invited: data.invited || [],
        alreadyMembers: data.alreadyMembers || [],
        totalInvited: data.totalInvited || 0,
        message: data.message || 'Invitations dispatched successfully.',
      });

      if (onInviteSuccess) {
        onInviteSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while sending invites.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setEmailInput('');
    setSelectedRole('member');
    setCustomRoleName('');
    setIsAddingCustomRole(false);
    setIsRoleMenuOpen(false);
    setSelectedDeptId('');
    setError(null);
    setInviteResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-surface-900 rounded-2xl shadow-2xl border border-surface-200 dark:border-surface-800 w-full max-w-lg overflow-hidden transition-all">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h3 className="text-lg font-bold text-surface-900 dark:text-surface-100 tracking-tight">
            Invite people for free
          </h3>
          <button
            onClick={handleReset}
            className="p-1 rounded-full text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5">
          {inviteResult ? (
            /* Results Screen */
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs text-emerald-800 dark:text-emerald-300">
                <div className="font-bold flex items-center space-x-1.5 text-sm mb-1">
                  <Check className="w-4 h-4 text-emerald-600" />
                  <span>{inviteResult.message}</span>
                </div>
                <p className="opacity-90">
                  New users have been added to IHB Pulse and invited with credentials.
                </p>
              </div>

              {inviteResult.alreadyMembers.length > 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl text-xs text-amber-800 dark:text-amber-300">
                  <span className="font-bold">Already members: </span>
                  {inviteResult.alreadyMembers.join(', ')}
                </div>
              )}

              {inviteResult.invited.length > 0 && (
                <div className="border border-surface-200 dark:border-surface-700 rounded-xl overflow-hidden divide-y divide-surface-200 dark:divide-surface-800">
                  <div className="p-2.5 bg-surface-50 dark:bg-surface-800 text-[11px] font-bold text-surface-600 dark:text-surface-300 uppercase tracking-wider flex items-center justify-between">
                    <span>Dispatched Credentials Summary</span>
                    <span className="text-[10px] font-normal lowercase opacity-75">
                      {inviteResult.invited.every((i) => i.emailDelivered) ? 'delivered via email' : 'share manually with invitee'}
                    </span>
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y divide-surface-100 dark:divide-surface-800">
                    {inviteResult.invited.map((inv) => (
                      <div key={inv.email} className="p-3.5 text-xs space-y-2">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                          <div>
                            <div className="font-bold text-sm text-surface-900 dark:text-surface-100">{inv.email}</div>
                            <div className="text-[11px] text-surface-500">
                              Role: <span className="capitalize font-semibold text-surface-700 dark:text-surface-300">{inv.role}</span>
                            </div>
                          </div>

                          <button
                            onClick={() => handleCopy(`Email: ${inv.email}\nTemporary Password: ${inv.generatedPassword}\nVerification Code: ${inv.verificationCode}\nLogin URL: ${window.location.origin}`, inv.email)}
                            className="px-3 py-1.5 text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white rounded-lg flex items-center space-x-1.5 shrink-0 transition-colors shadow-sm"
                          >
                            {copiedKey === inv.email ? (
                              <>
                                <CheckCheck className="w-3.5 h-3.5" />
                                <span>Copied Credentials!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-3.5 h-3.5" />
                                <span>Copy Credentials</span>
                              </>
                            )}
                          </button>
                        </div>

                        {/* Credential Details Pills */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-surface-50 dark:bg-surface-800/60 p-2.5 rounded-xl border border-surface-200/70 dark:border-surface-700/60">
                          <div>
                            <div className="text-[10px] uppercase font-bold text-surface-400">Temporary Password</div>
                            <div className="font-mono font-bold text-xs text-surface-900 dark:text-surface-100 select-all">
                              {inv.generatedPassword}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase font-bold text-surface-400">Verification Code</div>
                            <div className="font-mono font-bold text-xs text-brand-600 dark:text-brand-400 select-all">
                              {inv.verificationCode}
                            </div>
                          </div>
                        </div>

                        {/* Delivery Status Banner */}
                        <div className="text-[11px] pt-0.5">
                          {inv.emailDelivered ? (
                            <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center space-x-1">
                              <Check className="w-3.5 h-3.5" />
                              <span>Email successfully delivered via SMTP server.</span>
                            </span>
                          ) : (
                            <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-amber-800 dark:text-amber-300">
                              <span className="font-bold">✉️ Live email was NOT sent:</span> SMTP is not configured in server environment variables. Please share the password and verification code above directly with the invitee so they can sign in.
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Invite Form */
            <>
              {error && (
                <div className="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-xl text-xs text-rose-700 dark:text-rose-300 flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Email Input Field */}
              <div>
                <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <textarea
                    rows={2}
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="Enter email, comma or space separated"
                    className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all resize-none font-sans"
                  />
                </div>
                {parsedEmails.length > 0 && (
                  <p className="text-[11px] text-surface-500 mt-1">
                    {parsedEmails.length} valid email address{parsedEmails.length === 1 ? '' : 'es'} ready to invite.
                  </p>
                )}
              </div>

              {/* Invite As Role Selector */}
              <div>
                <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1.5">
                  Invite as
                </label>

                <div className="relative">
                  {/* Selected Role Trigger Card */}
                  <div
                    onClick={() => setIsRoleMenuOpen(!isRoleMenuOpen)}
                    role="button"
                    tabIndex={0}
                    className="p-3 rounded-xl bg-surface-50 hover:bg-surface-100/80 dark:bg-surface-800 dark:hover:bg-surface-800/80 border border-surface-200 dark:border-surface-700 cursor-pointer flex items-center justify-between transition-colors select-none"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 rounded-xl bg-white dark:bg-surface-700 flex items-center justify-center border border-surface-200 dark:border-surface-600 text-surface-500 shadow-sm shrink-0">
                        <UserIcon className="w-4 h-4 text-surface-600 dark:text-surface-300" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-bold text-surface-900 dark:text-surface-100">
                            {currentRoleConfig.name}
                          </span>
                          {currentRoleConfig.badge && (
                            <span
                              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                currentRoleConfig.badgeColor || 'bg-purple-100 text-purple-700'
                              }`}
                            >
                              {currentRoleConfig.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-surface-500 mt-0.5">
                          {currentRoleConfig.description}
                        </p>
                      </div>
                    </div>
                    <svg
                      className={`w-4 h-4 text-surface-400 transition-transform ${isRoleMenuOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {/* Dropdown Menu matching ClickUp */}
                  {isRoleMenuOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-elevated z-20 py-2 divide-y divide-surface-100 dark:divide-surface-700/60 animate-scale-up">
                      <div className="space-y-0.5">
                        {ROLE_OPTIONS.map((opt) => {
                          const isSelected = selectedRole === opt.id;
                          return (
                            <div
                              key={opt.id}
                              onClick={() => {
                                setSelectedRole(opt.id);
                                setIsRoleMenuOpen(false);
                                setIsAddingCustomRole(false);
                              }}
                              className={`px-4 py-2.5 flex items-start justify-between cursor-pointer transition-colors ${
                                isSelected
                                  ? 'bg-surface-50 dark:bg-surface-700/50'
                                  : 'hover:bg-surface-50 dark:hover:bg-surface-700/30'
                              }`}
                            >
                              <div className="flex items-start space-x-3">
                                <UserIcon className="w-4 h-4 mt-0.5 text-surface-500 shrink-0" />
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs font-bold text-surface-900 dark:text-surface-100">
                                      {opt.name}
                                    </span>
                                    {opt.badge && (
                                      <span
                                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${opt.badgeColor}`}
                                      >
                                        {opt.badge}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[11px] text-surface-500 mt-0.5">
                                    {opt.description}
                                  </p>
                                </div>
                              </div>
                              {isSelected && <Check className="w-4 h-4 text-surface-900 dark:text-surface-100 shrink-0 mt-0.5" />}
                            </div>
                          );
                        })}
                      </div>

                      {/* Add Custom Role Action */}
                      <div className="pt-1.5 px-2">
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingCustomRole(true);
                            setIsRoleMenuOpen(false);
                            setSelectedRole('custom');
                          }}
                          className="w-full text-left px-3 py-2 rounded-xl text-xs font-semibold text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700 flex items-center space-x-2 transition-colors"
                        >
                          <Plus className="w-4 h-4 text-surface-500" />
                          <span>Add custom role</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Custom Role Input Box */}
                {isAddingCustomRole && selectedRole === 'custom' && (
                  <div className="mt-2.5 p-3 rounded-xl bg-brand-50/50 dark:bg-brand-950/20 border border-brand-200 dark:border-brand-800/50 flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder="e.g. Design Collaborator, QA Lead, Client"
                      value={customRoleName}
                      onChange={(e) => setCustomRoleName(e.target.value)}
                      className="flex-1 px-3 py-1.5 text-xs rounded-lg bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!customRoleName.trim()) {
                          setSelectedRole('member');
                          setIsAddingCustomRole(false);
                        }
                      }}
                      className="px-2.5 py-1.5 text-xs text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* Assigned Department Selector */}
              <div>
                <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1.5 flex items-center justify-between">
                  <span>Department</span>
                  <span className="text-[10px] text-surface-400 font-normal">Optional</span>
                </label>
                <div className="relative">
                  <select
                    value={selectedDeptId}
                    onChange={(e) => setSelectedDeptId(e.target.value)}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  >
                    <option value="">
                      {departments.length === 0 ? 'No departments created yet (Optional)' : 'No Department Assigned'}
                    </option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-[11px] text-surface-400 mt-1">
                  Assigns the new member to an organizational department (created by admins).
                </p>
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-surface-50/50 dark:bg-surface-800/40 border-t border-surface-100 dark:border-surface-800 flex items-center justify-end space-x-3">
          {inviteResult ? (
            <button
              onClick={handleReset}
              className="px-5 py-2.5 rounded-xl bg-surface-900 hover:bg-black dark:bg-white dark:hover:bg-surface-100 text-white dark:text-surface-900 text-xs font-bold transition-all shadow-sm"
            >
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="px-4 py-2 text-xs font-semibold text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendInvite}
                disabled={isLoading || parsedEmails.length === 0}
                className="px-5 py-2.5 rounded-xl bg-[#121417] hover:bg-black text-white text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed shadow-sm flex items-center space-x-2 transition-all"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Sending invites...</span>
                  </>
                ) : (
                  <span>Send free invite</span>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
